/**
 * A thin drag divider between panels. Reports pointer deltas; the parent owns the
 * geometry (so clamping and persistence live in one place — useLayout). Uses
 * pointer capture so a drag keeps tracking outside the 1px hit area. A native
 * <button> for focusability + keyboard resize (arrow keys nudge the seam).
 *
 * Both orientations, because a layout is a tree now (layoutTree.ts): a `row`
 * split's seams are vertical lines dragged horizontally, a `column` split's are
 * horizontal lines dragged vertically. One component, one delta convention:
 * positive means "toward the end of the axis" (right, or down).
 */

import { useCallback, useRef } from 'react'

const KEY_STEP = 16

export function ResizeHandle({
  onDelta,
  ariaLabel,
  orientation = 'vertical',
  bare = false,
}: {
  /** Movement since the last event, in px, along the handle's axis. */
  onDelta: (delta: number) => void
  ariaLabel: string
  /** `vertical` = a vertical line between columns (drag left/right).
   *  `horizontal` = a horizontal line between rows (drag up/down). */
  orientation?: 'vertical' | 'horizontal'
  /** No resting line — for seams a neighbouring frame already draws. */
  bare?: boolean
}) {
  const last = useRef<number | null>(null)
  const horizontal = orientation === 'horizontal'
  const coord = useCallback(
    (e: React.PointerEvent) => (horizontal ? e.clientY : e.clientX),
    [horizontal],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      last.current = coord(e)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [coord],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (last.current === null) return
      const next = coord(e)
      const delta = next - last.current
      last.current = next
      if (delta !== 0) onDelta(delta)
    },
    [coord, onDelta],
  )

  const end = useCallback((e: React.PointerEvent) => {
    last.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const back = horizontal ? 'ArrowUp' : 'ArrowLeft'
      const forward = horizontal ? 'ArrowDown' : 'ArrowRight'
      if (e.key === back) {
        e.preventDefault()
        onDelta(-KEY_STEP)
      } else if (e.key === forward) {
        e.preventDefault()
        onDelta(KEY_STEP)
      }
    },
    [horizontal, onDelta],
  )

  return (
    <button
      type="button"
      // ARIA's name for a focusable seam is a window splitter (role="separator"
      // plus aria-valuenow), but a <button> cannot carry that role without also
      // carrying a value, and the seam's "value" is two panels' shares rather than
      // one number. A labelled, arrow-key-operable button is the honest fallback:
      // the label says which two panels it divides, which is the useful part.
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      className={`group relative shrink-0 border-0 p-0 ${
        horizontal ? 'h-px w-full cursor-row-resize' : 'w-px cursor-col-resize'
      } ${bare ? 'bg-transparent' : 'bg-border'}`}
    >
      {/* Widen the hit area without taking layout space. A 1px seam is a 1px
          target: the drag works and nobody can start it. 6px each side of the
          line is the smallest that reliably catches a pointer without the two
          neighbouring seams of a three-pane row overlapping. */}
      <span
        className={`absolute group-focus-visible:bg-blue-500/30 group-hover:bg-blue-500/20 ${
          horizontal ? '-inset-y-1.5 inset-x-0' : '-inset-x-1.5 inset-y-0'
        }`}
      />
    </button>
  )
}
